import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import DirectionArrow from 'components/Icons/DirectionArrow';
import MultiselectFilter from 'components/Others/MultiselectFilter';
import ProductCardwLensColour from 'components/ProductCard/ProductCardwLensColour';
import ProductCardwVTO from 'components/ProductCard/ProductCardwVTO';
import { CategoryLensTypes, FrameShape, SeoMetaTags, ShopPageCategoryFeaturedColor, ShopPageData, ShopPageFeaturedProduct, ShopPageSubcategory } from 'constants/dataTypes';
import { priceToNumber } from 'functions/bagPrice';
import { getAccessorieName } from 'functions/getProductName';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import { getPageHeaderData, getSeoMetaTags } from 'utils/common.utils';
import { generateImage } from 'utils/image.utils';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { getLocalCurrency } from '../../functions/currency';
import { wcApiDataService } from '../../functions/wcApi';
import useWindowSize from '../../hooks/useWindowSize';
import { addDataLayer } from '../../lib/ga';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { filterLensTypes } from 'functions/filterLensTypes';
import { changeSunglasses } from 'functions/changeSunglasses';

const VTOModalV2 = dynamic(
  () => import('components/VTO/VTOModalV2'),
  { ssr: false }
);
export default function Shop({
  shopPageData,
  seoMetaTags,
  categoryBannerProps,
  categoryLensTypes,
  nonPrescriptionSunglassesCategoryLensTypes
}: {
  shopPageData: ShopPageData;
  seoMetaTags: SeoMetaTags;
  categoryBannerProps: CategoryBannerProps;
  categoryLensTypes: CategoryLensTypes[];
  nonPrescriptionSunglassesCategoryLensTypes: CategoryLensTypes[];
}) {

  const compositeProducts: ShopPageSubcategory[] = shopPageData.sub_category_data;

  const data = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    }
  ];
  const { width } = useWindowSize();
  const router = useRouter();

  const selectedFilters = useMemo(() => {
    if (!router.query.filters || typeof router.query.filters !== 'string')
      return [];
    const filters = router.query.filters.split(',').map(filter =>
      filter === 'Non-prescription Sunglasses' ? 'Sunglasses' : filter
    );

    if (router.query.filters !== filters.join(',')) {
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, filters: filters.join(',') }
      }, undefined, { scroll: false });
    }

    return filters;
  }, [router.query]);

  const filterOptions = useMemo(() => {
    if (router.locale == 'international') {
      return [
        ...compositeProducts.map((category: any) => changeSunglasses(category.name as string, { capitalize: true })).filter((categoryName: string) => categoryName != "Contact Lenses")
      ];
    }
    return [
      ...compositeProducts.map((category: any) => changeSunglasses(category.name as string, { capitalize: true }))
    ];
  }, [compositeProducts, router]);


  const updateFilterValue = (newValue: string[]) => {
    router.query.filters = newValue.join();

    if (!router.query.filters.length) delete router.query.filters;

    router.replace(router, undefined, { scroll: false });
  };

  const isCategoryVisible = (categoryName: string) => {
    if (!selectedFilters.length) return true;

    return selectedFilters.includes(changeSunglasses(categoryName as string, { capitalize: true }));
  };

  const avaibleRoundShapeColors = [
    'denim-blue',
    'clear-frost',
    'koala-grey',
    'sepia-brown',
    'pink-lemonade'
  ];

  useEffect(() => {
    var categoryProducts: any = [];
    var position = 1;

    compositeProducts.map((category: ShopPageSubcategory) => {
      if (category && category.meta) {
        if (Array.isArray(category.meta.category_featured_colors)) {
          category.meta.category_featured_colors.map((color: ShopPageCategoryFeaturedColor) => {
            categoryProducts.push({
              name: category.name + ' - ' + color.name,
              price: category.meta?.category_price_reference?.price,
              regular_price: category.meta?.category_price_reference?.regular_price,
              category: category.name,
              variant: color.name,
              list: 'Shop',
              position: position
            });
            position++;
          });
        }

        category.meta?.featured_products && category.meta?.featured_products?.map((product: ShopPageFeaturedProduct) => {
          categoryProducts.push({
            name: product.name,
            price: product.meta.price,
            regular_price: product.meta.regular_price,
            category: category.name,
            list: 'Shop',
            position: position
          });
          position++;
        });


      }
    });

    addDataLayer({
      event: 'ImpCompleted',
      ecommerce: {
        currencyCode: getLocalCurrency(router.locale),
        impressions: categoryProducts
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const [categorySlug, setCategorySlug] = useState('');
  const [showVTOModal, setShowVTOModal] = useState(false);

  return (
    <div>
      <Head>
        <title>Shop | Dresden Vision</title>
        <meta
          name="description"
          content="Affordable, sustainable, Australian-made eyewear. Distance or reading glasses from $100; multifocals from $250."></meta>
        <meta property="og:image:width" content="1242" />
        <meta
          property="og:description"
          content="Affordable, sustainable, Australian-made eyewear. Distance or reading glasses from $100; multifocals from $250."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="719" />
        <meta name="twitter:title" content="Shop | Dresden Vision" />
        <meta name="og:title" content="Shop | Dresden Vision" />
      </Head>
      <CategoryBanner {...categoryBannerProps} breadcrumbItems={data} />
      <div className="mx-auto max-w-[1224px] px-4 pt-2">
        <MultiselectFilter
          value={selectedFilters}
          options={filterOptions}
          onUpdateValue={updateFilterValue}
        />
        {compositeProducts.map(
          (category: ShopPageSubcategory, i: number) =>
            category &&
            !(router.locale == 'international' && category.slug == "contact-lenses") &&
            isCategoryVisible(category.name) && (
              <div key={i} className="mb-24 flex flex-col">
                <Link
                  href={category.slug === 'spare-parts' ? `/shop/spare-parts` : `/shop/${changeSunglasses(category.name as string, { slug: true })}`}
                  className="group mb-6 flex items-center smx:flex-col smx:items-start">
                  <h2 className="font-sans text-2xl font-bold text-brand-black100">
                    {changeSunglasses(category.name as string, { capitalize: true })}
                  </h2>
                  <div className="ml-[32px] flex items-center justify-center pt-0.5 smx:ml-0 smx:mt-2">
                    <span className="flex items-center text-[16px] font-medium text-brand-blue transition-colors group-hover:text-brand-orange">
                      See All
                      <DirectionArrow
                        className="ml-[10px]"
                        fill="currentColor"
                      />
                    </span>
                  </div>
                </Link>
                <div className="grid grid-cols-3 gap-x-1 mdx:grid-cols-2 mdx:gap-y-1 xsmx:grid-cols-1 xsmx:gap-y-4">
                  {(category.meta?.category_frame_list?.length ?? 0) > 0 &&
                    category.meta?.category_frame_list?.slice(0, 3).map(
                      (frame: any, index: number) => (
                        <LazyHydrate whenVisible key={`${index}_${frame.frame_color.name}_${frame.frame_shape}`}>
                          <ProductCardwVTO
                            onClickProduct={() => {
                              setCategorySlug(() => category.slug);
                              addDataLayer({
                                event: 'productClick',
                                ecommerce: {
                                  click: {
                                    actionField: { list: 'Shop' },
                                    products: [
                                      {
                                        name: `${category.name} - ${frame.frame_color.name} - ${frame.frame_shape}`,
                                        price: category.meta?.category_price_reference?.price,
                                        category: category.name,
                                        variant: frame.frame_color.name,
                                        list: 'Shop',
                                        position: index
                                      }
                                    ]
                                  }
                                }
                              });
                            }}
                            width={width}
                            categoryLensTypes={category.slug === 'non-prescription-sunglasses' ? nonPrescriptionSunglassesCategoryLensTypes : categoryLensTypes}
                            data={{
                              title: `${frame.frame_color.name}`,
                              customTitle: category.name,
                              categorySlug: category.slug,
                              colorSlug: frame.frame_color.slug,
                              colors: category.meta?.category_glasses_color,
                              price: category.meta?.category_price_reference?.price,
                              regularPrice: category.meta?.category_price_reference?.regular_price,
                              href: `/shop/${changeSunglasses(category.name as string, { slug: true })}/${frame.frame_color.slug}`,
                              isPriority: width > 600 ? i <= 1 : i == 0,
                              shape: frame.frame_shape as FrameShape
                            }}
                            onVTOToggle={() => {
                              setCategorySlug(category.slug);
                              setShowVTOModal(true);
                            }}
                          />
                        </LazyHydrate>
                      )
                    )}

                  {(category.slug === 'accessories' || category.slug === 'spare-parts' || category.slug === 'contact-lenses') &&
                    ((category.slug === 'accessories' || category.slug === 'contact-lenses') ?
                      (Array.isArray(category.meta?.featured_products) ?
                        category.meta?.featured_products?.map((prod: any, index: number) => (
                          <ProductCardwLensColour
                            onClickPoroduct={() => {
                              addDataLayer({
                                event: 'productClick',
                                ecommerce: {
                                  click: {
                                    actionField: { list: 'Shop' },
                                    products: [
                                      {
                                        name: getAccessorieName(prod.name),
                                        price: priceToNumber(prod?.meta.price),
                                        category: category.name,
                                        list: 'Shop',
                                        position: index
                                      }
                                    ]
                                  }
                                }
                              });
                            }}
                            width={width}
                            key={index}
                            data={{
                              images: prod?.meta.product_images,
                              title: getAccessorieName(prod.name),
                              price: prod?.meta.price,
                              regularPrice: prod.meta.regular_price,
                              href: `/shop/${category.slug}/${prod.slug.replace('large-', '')}`,
                              prio: false
                            }}
                          />
                        ))
                        : null
                      )
                      :
                      category.meta?.category_glasses_color?.slice(0, 3).map((color: any, index: number) => (
                        <ProductCardwLensColour
                          key={index}
                          onClickPoroduct={() => {
                            addDataLayer({
                              event: 'productClick',
                              ecommerce: {
                                click: {
                                  actionField: { list: 'Shop' },
                                  products: [
                                    {
                                      name: `Arms - ${color.name}`,
                                      price: category.meta?.category_price_reference?.price,
                                      category: category.name,
                                      list: 'Shop',
                                      position: index
                                    }
                                  ]
                                }
                              }
                            });
                          }}
                          width={width}
                          data={{
                            images: [],
                            title: color.name,
                            price: category.meta?.category_price_reference?.price,
                            regularPrice: category.meta?.category_price_reference?.regular_price,
                            href: `/shop/${category.slug}/${color.slug}`,
                            prio: false,
                          }}
                        />
                      ))
                    )}
                </div>
              </div>
            )
        )}


      </div>
      {showVTOModal && (
        <VTOModalV2
          categoryLensTypes={categoryLensTypes}
          nonPrescriptionSunglassesCategoryLensTypes={nonPrescriptionSunglassesCategoryLensTypes}
        />
      )}
      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {

  let shopPageData: ShopPageData = await wcApiDataService('cat/shop', 'hasSubCategory', locale);

  let productDetailPageData: any = await wcApiDataService('glasses/' + 'prescription-sunglasses', '', locale);
  const glassesProductData = productDetailPageData?.components;

  let nonPrescriptionSunglassesProduct: any = await wcApiDataService('glasses/' + 'non-prescription-sunglasses', '', locale);
  const nonPrescriptionSunglassesProductData = nonPrescriptionSunglassesProduct?.components;

  const categoryLensTypes: CategoryLensTypes[] = filterLensTypes(glassesProductData);
  const nonPrescriptionSunglassesCategoryLensTypes: CategoryLensTypes[] = filterLensTypes(nonPrescriptionSunglassesProductData);

  if (!shopPageData) {
    return {
      notFound: true
    };
  }

  const seoMetaTags = await getSeoMetaTags('/shop', locale);

  const categoryBannerProps: CategoryBannerProps = {
    title: 'Shop Dresden',
    description: shopPageData.description,
    imageSrc: generateImage(shopPageData.slug, 'categories', 'slug', 'banner', 'jpg')
  }

  const notionPageHeaders = await getPageHeaderData('shop', locale, categoryBannerProps);

  return {
    props: {
      shopPageData,
      seoMetaTags,
      categoryLensTypes,
      nonPrescriptionSunglassesCategoryLensTypes,
      categoryBannerProps: notionPageHeaders
    }
  };
};
